import { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PackageConfig } from '@hooks/common/useBilling';
import styled, { css, keyframes } from 'styled-components';
import { CountUp } from 'use-count-up';

import AddCreditsModal from './AddCreditsModal';
import { useTheme } from '@/components/theme-v2/ThemeContext';

// ============================================
// ANIMATIONS
// ============================================

// Subtle hover glow
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(0, 200, 224, 0.2); }
  50% { box-shadow: 0 0 16px rgba(0, 200, 224, 0.4); }
`;

// Credit added - emerald success pulse
const creditAddedPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
    border-color: rgba(16, 185, 129, 0.8);
  }
  25% {
    box-shadow: 0 0 20px 4px rgba(16, 185, 129, 0.4);
  }
  50% {
    box-shadow: 0 0 30px 8px rgba(16, 185, 129, 0.2);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
    border-color: rgba(0, 200, 224, 0.3);
  }
`;

// Credit deducted - warm amber notification pulse
const creditDeductedPulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.6);
    border-color: rgba(245, 158, 11, 0.7);
  }
  25% {
    box-shadow: 0 0 16px 3px rgba(245, 158, 11, 0.35);
  }
  50% {
    box-shadow: 0 0 24px 6px rgba(245, 158, 11, 0.15);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
    border-color: rgba(0, 200, 224, 0.3);
  }
`;

// Floating delta indicator - rises and fades
const floatUp = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  20% {
    opacity: 1;
    transform: translateY(-8px) scale(1.05);
  }
  100% {
    opacity: 0;
    transform: translateY(-32px) scale(0.9);
  }
`;

// Number highlight shimmer
const shimmer = keyframes`
  0% { background-position: -100% 0; }
  100% { background-position: 200% 0; }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const TokensWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const TokensButtonGroup = styled.div<{
  $isDark?: boolean;
  $changeType?: 'added' | 'deducted' | null;
}>`
  display: flex;
  align-items: stretch;
  border-radius: 10px;
  overflow: visible;
  background: ${props =>
    props.$isDark ? 'rgba(21, 28, 36, 0.85)' : 'linear-gradient(135deg, #00c8e0 0%, #007d8f 100%)'};
  border: 1.5px solid ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.35)' : 'transparent')};
  transition:
    border-color 0.3s ease,
    box-shadow 0.3s ease;

  ${props =>
    props.$changeType === 'added' &&
    css`
      animation: ${creditAddedPulse} 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    `}

  ${props =>
    props.$changeType === 'deducted' &&
    css`
      animation: ${creditDeductedPulse} 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    `}

  &:hover {
    border-color: ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.6)' : 'rgba(255, 255, 255, 0.3)')};
  }
`;

const TokensDisplay = styled.button<{
  $isDark?: boolean;
  $isChanging?: boolean;
  $changeType?: 'added' | 'deducted' | null;
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  color: ${props => (props.$isDark ? '#00c8e0' : '#ffffff')};
  font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', monospace;
  font-size: 0.9rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 110px;

  /* Shimmer effect during number change */
  ${props =>
    props.$isChanging &&
    css`
      &::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          ${props.$changeType === 'added' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(245, 158, 11, 0.12)'} 50%,
          transparent 100%
        );
        background-size: 200% 100%;
        animation: ${shimmer} 1s ease-out;
        pointer-events: none;
        border-radius: 8px;
      }
    `}

  &:hover {
    background: ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.12)' : 'rgba(255, 255, 255, 0.12)')};
  }

  @media (max-width: 600px) {
    padding: 8px 12px;
    font-size: 0.8rem;
    min-width: 90px;
  }
`;

const TokenValue = styled.span<{
  $changeType?: 'added' | 'deducted' | null;
  $isDark?: boolean;
}>`
  display: inline-flex;
  transition: color 0.3s ease;

  ${props =>
    props.$changeType === 'added' &&
    css`
      color: ${props.$isDark ? '#34d399' : '#a7f3d0'};
    `}

  ${props =>
    props.$changeType === 'deducted' &&
    css`
      color: ${props.$isDark ? '#fbbf24' : '#fef3c7'};
    `}
`;

const TokenLabel = styled.span`
  opacity: 0.9;
  font-weight: 500;
`;

const AddButton = styled.button<{ $isDark?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  background: ${props =>
    props.$isDark ? 'linear-gradient(135deg, #00c8e0 0%, #007d8f 100%)' : 'rgba(255, 255, 255, 0.18)'};
  border: none;
  border-left: 1px solid ${props => (props.$isDark ? 'rgba(0, 200, 224, 0.25)' : 'rgba(255, 255, 255, 0.25)')};
  color: ${props => (props.$isDark ? '#0a0e14' : '#ffffff')};
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: ${props =>
      props.$isDark ? 'linear-gradient(135deg, #41e2f7 0%, #00c8e0 100%)' : 'rgba(255, 255, 255, 0.28)'};
    animation: ${pulseGlow} 1.5s ease-in-out infinite;
  }

  svg {
    width: 18px;
    height: 18px;
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: scale(1.1);
  }

  @media (max-width: 600px) {
    padding: 8px 12px;
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

// Floating delta indicator
const DeltaIndicator = styled.div<{
  $type: 'added' | 'deducted';
  $isDark?: boolean;
}>`
  position: absolute;
  top: -4px;
  left: 50%;
  transform: translateX(-50%);
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 12px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 100;
  animation: ${floatUp} 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;

  ${props =>
    props.$type === 'added' &&
    css`
      color: ${props.$isDark ? '#10b981' : '#059669'};
      background: ${props.$isDark ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.9)'};
      ${!props.$isDark && 'color: #ffffff;'}
      box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
    `}

  ${props =>
    props.$type === 'deducted' &&
    css`
      color: ${props.$isDark ? '#f59e0b' : '#d97706'};
      background: ${props.$isDark ? 'rgba(245, 158, 11, 0.15)' : 'rgba(245, 158, 11, 0.9)'};
      ${!props.$isDark && 'color: #ffffff;'}
      box-shadow: 0 2px 8px rgba(245, 158, 11, 0.3);
    `}
`;

// Plus Icon
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

// ============================================
// COMPONENT
// ============================================

interface DeltaDisplay {
  id: number;
  value: number;
  type: 'added' | 'deducted';
}

const AddTokensButton: FC<{
  config: PackageConfig[];
  tokensAmount?: number;
  isDisabledAction: boolean;
  isLoading: boolean;
  isLargerScreen: boolean;
}> = ({
  config,
  isDisabledAction,
  isLoading,
  tokensAmount = 0,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  isLargerScreen,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [changeType, setChangeType] = useState<'added' | 'deducted' | null>(null);
  const [deltas, setDeltas] = useState<DeltaDisplay[]>([]);
  const { t } = useTranslation();
  const previousAmount = useRef(tokensAmount);
  const tokensBalance = useRef(tokensAmount);
  const deltaIdCounter = useRef(0);
  const checkoutHandled = useRef(false);
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  // Detect checkout success from URL params (after redirect from Stripe/mock checkout)
  useEffect(() => {
    if (checkoutHandled.current) return;

    const urlParams = new URLSearchParams(window.location.search);
    const checkoutStatus = urlParams.get('checkout');
    const tokensAdded = urlParams.get('tokens');

    if ((checkoutStatus === 'success' || checkoutStatus === 'mock_success') && tokensAdded) {
      checkoutHandled.current = true;
      const addedAmount = parseInt(tokensAdded, 10);

      if (!Number.isNaN(addedAmount) && addedAmount > 0) {
        // Small delay to ensure component is mounted and visible
        setTimeout(() => {
          // Trigger the "added" animation
          setChangeType('added');

          // Add floating delta indicator
          deltaIdCounter.current += 1;
          const newDelta: DeltaDisplay = {
            id: deltaIdCounter.current,
            value: addedAmount,
            type: 'added',
          };
          setDeltas(prev => [...prev, newDelta]);

          // Clear change type after animation
          setTimeout(() => setChangeType(null), 900);

          // Remove delta indicator after fade out
          setTimeout(() => {
            setDeltas(prev => prev.filter(d => d.id !== newDelta.id));
          }, 2000);
        }, 500);

        // Clean up URL params (remove checkout params without full page reload)
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, []);

  // Detect and handle token changes from API polling
  useEffect(() => {
    const prevAmount = previousAmount.current;
    const currentAmount = tokensAmount;

    if (prevAmount !== currentAmount && prevAmount !== 0) {
      const delta = currentAmount - prevAmount;
      const type = delta > 0 ? 'added' : 'deducted';

      // Trigger change animation
      setChangeType(type);

      // Add floating delta indicator
      deltaIdCounter.current += 1;
      const newDelta: DeltaDisplay = {
        id: deltaIdCounter.current,
        value: Math.abs(delta),
        type,
      };
      setDeltas(prev => [...prev, newDelta]);

      // Clear change type after animation completes
      const timer = setTimeout(() => {
        setChangeType(null);
      }, 900);

      // Remove delta indicator after it fades out
      const deltaTimer = setTimeout(() => {
        setDeltas(prev => prev.filter(d => d.id !== newDelta.id));
      }, 2000);

      return () => {
        clearTimeout(timer);
        clearTimeout(deltaTimer);
      };
    }

    previousAmount.current = currentAmount;
  }, [tokensAmount]);

  // Update balance ref for CountUp
  useEffect(() => {
    if (tokensAmount !== undefined) {
      tokensBalance.current = tokensAmount;
    }
  }, [tokensAmount]);

  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Format delta display
  const formatDelta = (value: number, type: 'added' | 'deducted') => {
    const prefix = type === 'added' ? '+' : '-';
    return `${prefix}${value.toLocaleString()}`;
  };

  return (
    <>
      <TokensWrapper data-tour="tour-tokens">
        {/* Floating delta indicators */}
        {deltas.map(delta => (
          <DeltaIndicator key={delta.id} $type={delta.type} $isDark={isDarkMode}>
            {formatDelta(delta.value, delta.type)}
          </DeltaIndicator>
        ))}

        <TokensButtonGroup $isDark={isDarkMode} $changeType={changeType}>
          <TokensDisplay
            onClick={handleOpenModal}
            aria-label="Current token balance"
            $isDark={isDarkMode}
            $isChanging={changeType !== null}
            $changeType={changeType}
          >
            <TokenValue $changeType={changeType} $isDark={isDarkMode}>
              <CountUp
                isCounting
                key={tokensAmount}
                start={tokensBalance.current}
                end={tokensAmount}
                duration={changeType ? 1.2 : 0.5}
                easing="easeOutCubic"
                updateInterval={0}
                formatter={value => Math.round(value).toLocaleString()}
              />
            </TokenValue>
            <TokenLabel>{t('common.tokens')}</TokenLabel>
          </TokensDisplay>
          <AddButton onClick={handleOpenModal} aria-label="Add more tokens" $isDark={isDarkMode}>
            <PlusIcon />
          </AddButton>
        </TokensButtonGroup>
      </TokensWrapper>

      <AddCreditsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        config={config}
        tokensAmount={tokensAmount}
        isDisabledAction={isDisabledAction}
        isLoading={isLoading}
      />
    </>
  );
};

export default memo(AddTokensButton);
